import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Pagination,
  Menu,
  MenuItem,
  styled,
  alpha,
  useTheme,
  useMediaQuery,
  Tooltip,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Addnewcreditor from "./Addnewcreditor";

// Styled components
const ModernTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 500,
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    transition: "background-color 0.2s ease",
  },
}));

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "600px",
  overflowX: "auto",
  "&::-webkit-scrollbar": {
    height: "8px",
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.primary.main, 0.3),
    borderRadius: "4px",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.5),
    },
  },
}));

function Creditors() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [creditors, setCreditors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: "id",
    direction: "asc",
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCreditor, setSelectedCreditor] = useState(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const creditorsPerPage =4;

  const PRIMARY_COLOR = "#0a2e52";
  const SECONDARY_COLOR = "#1565C0";

  useEffect(() => {
    fetchCreditors();
  }, []);

  const fetchCreditors = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/support/creditors");
      const data = await res.json();
      setCreditors(data.recordset || data);
    } catch (error) {
      console.error("Error fetching creditors:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = "desc";
    if (sortConfig.key === key) {
      direction = sortConfig.direction === "desc" ? "asc" : "desc";
    }
    setSortConfig({ key, direction });
  };

  const filteredCreditors = creditors.filter((creditor) => {
    const search = searchTerm.toLowerCase();
    return (
      creditor.creditor_Name?.toLowerCase().includes(search) ||
      creditor.acountNumber?.toLowerCase().includes(search) ||
      creditor.bank?.toLowerCase().includes(search)
    );
  });

  const sortedCreditors = [...filteredCreditors].sort((a, b) => {
    if (sortConfig.key) {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
    }
    return 0;
  });

  const indexOfLastCreditor = currentPage * creditorsPerPage;
  const indexOfFirstCreditor = indexOfLastCreditor - creditorsPerPage;
  const currentCreditors = sortedCreditors.slice(
    indexOfFirstCreditor,
    indexOfLastCreditor,
  );

  const handleMenuOpen = (event, creditor) => {
    setAnchorEl(event.currentTarget);
    setSelectedCreditor(creditor);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCreditor(null);
  };

  const handleAddNew = () => {
    setOpenAddModal(true);
  };

  const handleEdit = () => {
    console.log("Edit creditor:", selectedCreditor);
    handleMenuClose();
  };

  const handleDelete = () => {
    console.log("Delete creditor:", selectedCreditor);
    handleMenuClose();
  };

  const handleSaveCreditor = (newCreditor) => {
    setCreditors((prev) => [...prev, { ...newCreditor, id: prev.length + 1 }]);
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "16px",
        p: 3,
        mb: 3,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Creditors
        </Typography>

        <TextField
          size="small"
          placeholder="Search by name, bank account, or bank..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          sx={{ minWidth: 280 }}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* <Typography variant="body2" color="text.secondary">
            {filteredCreditors.length}{" "}
            {filteredCreditors.length === 1 ? "creditor" : "creditors"} found
          </Typography> */}

          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
          >
            Add New
          </Button>
        </Box>
      </Box>

      <ResponsiveTableContainer component={Paper}>
        <Table size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: theme.palette.primary.light,
                "& th": {
                  color: "white",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                },
              }}
            >
              {[
                { id: "id", label: "ID" },
                { id: "creditor_Name", label: "Name" },
                { id: "calculationMethod", label: "Calculation Method" },
                { id: "basepayrollcomponent", label: "Base Payroll" },
                { id: "rate", label: "Rate" },
                { id: "acountNumber", label: "Bank Account Number" },
                { id: "bank", label: "Bank" },
                { id: "actions", label: "Actions" },
              ].map((column) => (
                <ModernTableCell
                  key={column.id}
                  onClick={() =>
                    column.id !== "actions" && handleSort(column.id)
                  }
                  sx={{
                    cursor: column.id !== "actions" ? "pointer" : "default",
                  }}
                >
                  <Box display="flex" alignItems="center">
                    {column.label}
                  </Box>
                </ModernTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <ModernTableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Loading creditors...
                  </Typography>
                </ModernTableCell>
              </TableRow>
            ) : currentCreditors.length > 0 ? (
              currentCreditors.map((creditor) => (
                <StyledTableRow key={creditor.id} hover>
                  <ModernTableCell>{creditor.id}</ModernTableCell>
                  <ModernTableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {creditor.creditor_Name}
                    </Typography>
                  </ModernTableCell>
                  <ModernTableCell>
                    {creditor.calculationMethod}
                  </ModernTableCell>
                  <ModernTableCell>
                    {creditor.basepayrollcomponent}
                  </ModernTableCell>
                  <ModernTableCell
                    sx={{ fontWeight: 600, color: PRIMARY_COLOR }}
                  >
                    {creditor.rate}
                  </ModernTableCell>
                  <ModernTableCell>{creditor.acountNumber}</ModernTableCell>
                  <ModernTableCell>{creditor.bank}</ModernTableCell>
                  <ModernTableCell>
                    <IconButton onClick={(e) => handleMenuOpen(e, creditor)}>
                      <MoreVertIcon />
                    </IconButton>
                  </ModernTableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <ModernTableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography>No creditors found</Typography>
                </ModernTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveTableContainer>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Pagination
          count={Math.ceil(filteredCreditors.length / creditorsPerPage)}
          page={currentPage}
          onChange={(e, value) => setCurrentPage(value)}
          color="primary"
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ color: PRIMARY_COLOR }} />
          <Typography variant="body2">Edit</Typography>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <DeleteIcon fontSize="small" color="error" />
          <Typography variant="body2" color="error">
            Delete
          </Typography>
        </MenuItem>
      </Menu>

      <Addnewcreditor
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        onSave={handleSaveCreditor}
      />
    </Box>
  );
}

export default Creditors;
